import React from 'react';
import styled, { css } from 'styled-components';

import { DocumentationLink } from 'components/support';
import DocsHelper from 'util/DocsHelper';
import useSearchVersionCheck from 'hooks/useSearchVersionCheck';
import { ExternalLink } from 'components/common';

const StyledUl = styled.ul(
  ({ theme }) => css`
    padding-left: 20px;
    list-style-type: disc;

    > li {
      margin-bottom: ${theme.spacings.sm};
    }
    > li:last-child {
      margin-bottom: 0;
    }
  `,
);

const WarmTierRepositorySetupText = () => {
  const { data, isLoading } = useSearchVersionCheck('datanode');

  const showDataNodeText = !isLoading && data?.satisfied;

  return (
    <>
      <p>
        Repository locations will not be shown until they are configured, which is performed outside of the Graylog UI.
      </p>
      <p>
        The process for setting up a Warm Tier Repository is documented fully{' '}
        <DocumentationLink page={DocsHelper.PAGES.DATA_TIERING_WARM_TIER_SETUP} text="here" />.
      </p>
      <p>In summary:</p>

      {showDataNodeText ? (
        <StyledUl>
          <li>
            If using Google Cloud Storage, on <strong>all</strong> search nodes, the Service Account Key JSON should be
            placed in the same directory as the datanode.conf file
          </li>
          <li>
            In Datanode.conf, for <strong>all</strong> search nodes, specify the required keys in the datanode.conf
            files
            <br />
            For s3: <code>s3_client_default_access_key</code>, <code>s3_client_default_secret_key</code>,{' '}
            <code>s3_client_default_endpoint</code>
            <br />
            For Google Cloud Storage: <code>gcs_credentials_file</code>
            <br />
            For Network Storage: <code>path_repo</code>
          </li>
          <li>
            Restart <strong>all</strong> search nodes for changes to take effect.
          </li>
          <li>
            In the Graylog UI, return to this page. You should now find that a &quot;Create new warm storage
            repository&quot; button is shown in place of this warning,
          </li>
        </StyledUl>
      ) : (
        <StyledUl>
          <li>
            If planning to use AWS S3 or Google Cloud Storage, first install the corresponding Opensearch Plugin, on{' '}
            <strong>all</strong> search nodes.
          </li>
          <li>
            For a AWS S3 data tiering backend, add your AWS access and secret keys to the OpenSearch keystore on{' '}
            <strong>all</strong> search nodes - see Opensearch docs on this step{' '}
            <ExternalLink href="https://docs.opensearch.org/docs/2.13/tuning-your-cluster/availability-and-recovery/snapshots/snapshot-restore/#register-repository">
              here
            </ExternalLink>
            .
          </li>
          <li>
            For a Google Cloud Storage Data Tiering backend, add the Service Account Key JSON to the OpenSearch keystore
            on all search nodes - see Opensearch docs on this step{' '}
            <ExternalLink href="https://opster.com/guides/opensearch/opensearch-operations/how-to-set-up-snapshot-repositories/#How-to-configure-a-snapshot-repository-for-Google-Cloud-Storage">
              here
            </ExternalLink>
            .
          </li>
          <li>
            Make documented changes to the Opensearch configuration file on <strong>all</strong> search nodes.
          </li>
          <li>
            Restart <strong>all</strong> search nodes.
          </li>
          <li>
            In the Graylog UI, return to this page. You should now find that a &quot;Create new warm storage
            repository&quot; button is shown in place of this warning,
          </li>
        </StyledUl>
      )}
    </>
  );
};

export default WarmTierRepositorySetupText;
